import { differenceWith, find, isEqual, omit } from 'lodash';
import { all, call, put, select, takeEvery } from 'redux-saga/effects';

import { REDUX_STORE } from '@funnels/constants';
import {
  Asset,
  CreateAssetAction,
  CustomerData,
  DeleteAssetAction,
  FetchAssetsAction,
  ReduxStoreData,
  ServerAssetUpdate,
  StoreApplicant,
  UpdateAssetAction,
  UpsertAssetsAction,
} from '@funnels/types';
import { datadogUtil } from '@funnels/utils';

import {
  addDefaultOwners,
  getAssetId,
  sortByCreatedDateAsc,
  waitForAuth,
  waitForResourcesToBeCreated,
} from '../_common';
import { createConveyancingPropertyId } from '../conveyancing/actions';
import {
  createAsset,
  createAssetFail,
  createAssetSuccess,
  deleteAssetFail,
  deleteAssetSuccess,
  fetchAssetsFail,
  fetchAssetsSuccess,
  setAssetError,
  updateAsset,
  updateAssetAbort,
  updateAssetFail,
  updateAssetSuccess,
  upsertAssetsFail,
  upsertAssetsSuccess,
} from './actions';
import AssetAPI from './api';
import { cleanupAsset } from './utils';

export function* createAssetWorker(action: CreateAssetAction): any {
  try {
    yield waitForAuth();

    const { applicationId } = action;
    const { clientId, ...asset } = action.asset;
    const response = yield call(AssetAPI.create, applicationId, asset);

    yield put(createAssetSuccess(clientId, response.data.id));
    yield put(createConveyancingPropertyId(response.data.id));
  } catch (e: any) {
    datadogUtil.addError(e);
    const {
      asset: { clientId },
    } = action;
    yield all([put(createAssetFail(clientId)), put(setAssetError(e.response?.data || e.message))]);
  }
}

export function* deleteAssetWorker(action: DeleteAssetAction): any {
  try {
    yield waitForAuth();
    const { applicationId, clientId, id } = action;
    const assetId = yield getAssetId(clientId, id);

    yield call(AssetAPI.del, applicationId, assetId);
    yield put(deleteAssetSuccess());
  } catch (e: any) {
    datadogUtil.addError(e);
    const { clientId } = action;
    yield all([put(deleteAssetFail(clientId)), put(setAssetError(e.response?.data || e.message))]);
  }
}

export function* fetchAssetsWorker(action: FetchAssetsAction): any {
  try {
    yield waitForAuth();

    const { applicationId } = action;
    const response = yield call(AssetAPI.fetch, applicationId);

    const assets = response.data.map((asset: any) => omit(asset, ['meta', 'updatedAt']));
    assets.sort(sortByCreatedDateAsc);

    yield put(fetchAssetsSuccess(assets));
  } catch (e: any) {
    datadogUtil.addError(e);
    yield all([put(fetchAssetsFail()), put(setAssetError(e.response?.data || e.message))]);
  }
}

export function* updateAssetWorker(action: UpdateAssetAction): any {
  try {
    yield waitForAuth();
    const { applicationId } = action;
    const assetId = yield getAssetId(action.asset.clientId, action.asset.id);

    const asset = omit(action.asset, [
      'clientId',
      'createdAt',
      'isDeleted',
      'isDeidentified',
      'applicationId',
      'id',
      'dataType',
      'relationId',
      'liabilityId',
    ]) as ServerAssetUpdate;

    // For whatever reason, the creation failed so we convert this update to a create
    if (!assetId) {
      yield all([put(updateAssetAbort()), put(createAsset(applicationId, action.asset))]);
    } else {
      yield call(AssetAPI.update, applicationId, assetId, omit(asset, 'rentalIncome', 'applicationId'));

      yield put(updateAssetSuccess());
    }
  } catch (e: any) {
    datadogUtil.addError(e);
    const {
      asset: { clientId },
    } = action;
    yield all([put(updateAssetFail(clientId)), put(setAssetError(e.response?.data || e.message))]);
  }
}

export function* upsertAssetsWorker(action: UpsertAssetsAction): any {
  try {
    yield waitForAuth();
    let reduxStoreApplicants = yield select((state: ReduxStoreData) => state.applicants.data);
    yield waitForResourcesToBeCreated({
      applicants: reduxStoreApplicants.map((a: StoreApplicant) => a.clientId),
    });

    reduxStoreApplicants = yield select((state: ReduxStoreData) => state.applicants.data);
    const { applicationId, assets } = action;
    const reduxStoreAssets: Asset[] = yield select((state: ReduxStoreData) => state.assets.data);
    const reduxStoreCustomer: CustomerData = yield select((state: ReduxStoreData) => state.customers?.data?.[0]);

    // Filter out Id in the comparator function because its possible for that to be added by the store
    const changedAssets = differenceWith(assets, reduxStoreAssets, ({ id: id1, ...rest1 }, { id: id2, ...rest2 }) =>
      isEqual(rest1, rest2)
    );
    yield all(
      changedAssets.map((a) => {
        // We rely on grabbing the id from the store so that
        // we DON'T rely on the store syncing to the form after initialisation
        const reduxStoreAsset = find(reduxStoreAssets, { clientId: a.clientId });
        const asset = cleanupAsset(a, { reduxStoreApplicants }) as Asset;
        const assetWithDefaultOwners = addDefaultOwners(asset, reduxStoreApplicants, reduxStoreCustomer) as Asset;

        if (!reduxStoreAsset) {
          // No asset
          return put(createAsset(applicationId, assetWithDefaultOwners));
        } else if (!reduxStoreAsset.id) {
          // Asset but no id, therefore create has been fired but success hasn't returned
          return put(updateAsset(applicationId, assetWithDefaultOwners));
        } else {
          // Asset is in the store and has an id
          return put(updateAsset(applicationId, { id: reduxStoreAsset.id, ...assetWithDefaultOwners }));
        }
      })
    );
    yield put(upsertAssetsSuccess());
  } catch (e: any) {
    datadogUtil.addError(e);
    yield all([put(upsertAssetsFail()), put(setAssetError(e.response?.data || e.message))]);
  }
}

export function* assetsWatcher() {
  yield takeEvery(REDUX_STORE.ASSETS.ACTION_TYPES.CREATE_ASSET, createAssetWorker);
  yield takeEvery(REDUX_STORE.ASSETS.ACTION_TYPES.DELETE_ASSET, deleteAssetWorker);
  yield takeEvery(REDUX_STORE.ASSETS.ACTION_TYPES.FETCH_ASSETS, fetchAssetsWorker);
  yield takeEvery(REDUX_STORE.ASSETS.ACTION_TYPES.UPDATE_ASSET, updateAssetWorker);
  yield takeEvery(REDUX_STORE.ASSETS.ACTION_TYPES.UPSERT_ASSETS, upsertAssetsWorker);
}

export default assetsWatcher;
